import React, { useEffect, useState } from "react";
import { CreditCard, Banknote, MailIcon } from "lucide-react";
import generateEmailBody from "../../common/EmailBody";
import sendEmail from "../../common/SendEmail";
import { QRCodeCanvas } from "qrcode.react";
import config from "../../config/config";
import { createRazorpayPaymentLink } from "../../common/RazorPayPartialPayment";

const {UPI_ID, UPI_NAME} = config;

const PaymentOptions = ({
  orderId,
  hemOrderId,
  total,
  customerData,
  API_BASE_URL,
  RAZOR_PAY_API_KEY,
  cartPayload,
  discount,
  actualPrice,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const upiURL = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(
    UPI_NAME,
  )}&am=${total}&cu=INR`;

  // Load Razorpay once
  useEffect(() => {
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // ---------------- ONLINE PAYMENT ----------------
  const handleOnlinePayment = () => {
    const options = {
      key: RAZOR_PAY_API_KEY,
      amount: total * 100,
      currency: "INR",
      name: "Edumitram Book Store",
      description: "Book Order Payment",
      order_id: orderId,

      handler: async function (response) {
        try {
          //setLoading(true);
          setProcessingPayment(true);

          const res = await fetch(`${API_BASE_URL}/api/Order/Capture-Payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              Status: "succeeded",
              Currency: "INR",
              Amount: total,
              PaymentMode: "online",
            }),
          });

          if (!res.ok) throw new Error("Payment capture failed");

          // ✅ Save AFTER DB success
          sessionStorage.setItem("paymentId", response.razorpay_payment_id);
          sessionStorage.setItem("hemOrderId", hemOrderId);

          const emailHTML = generateEmailBody(
            "Invoice Order Book",
            hemOrderId,
            total,
            customerData,
            null,
            cartPayload,
            discount,
            actualPrice,
          );

          await sendEmail(customerData.email, "Payment Successful", emailHTML);

          onSuccess();
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      },

      prefill: {
        name: customerData.name,
        email: customerData.email,
        contact: customerData.mobile,
      },
      theme: { color: "#16a34a" },
    };

    new window.Razorpay(options).open();
  };

  // ---------------- CASH PAYMENT ----------------
  const handleCashPayment = async () => {
    try {
      //setLoading(true);
      setProcessingPayment(true);

      const cashId = `cash_${Date.now()}`;

      const res = await fetch(`${API_BASE_URL}/api/Order/Capture-Payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId,
          paymentId: cashId,
          signature: "cash-signature",
          Status: "pending",
          Currency: "INR",
          Amount: total,
          PaymentMode: "cashPay",
        }),
      });

      if (!res.ok) throw new Error("Cash capture failed");

      // ✅ Save AFTER DB success
      sessionStorage.setItem("paymentId", cashId);
      sessionStorage.setItem("hemOrderId", hemOrderId);

      const emailHTML = generateEmailBody(
        "Cash Payment Recieved",
        hemOrderId,
        total,
        customerData,
        null,
        cartPayload,
        discount,
        actualPrice,
      );

      await sendEmail(customerData.email, "Cash Order Placed", emailHTML);

      onSuccess();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- PAY BY MAIL ----------------
  const handlePayByMail = async () => {
    try {
      //setLoading(true);
      setProcessingPayment(true);

      const paymentLink = await createRazorpayPaymentLink(
        orderId,
        total,
        customerData,
        API_BASE_URL,
      );

      const res = await fetch(`${API_BASE_URL}/api/Order/Capture-Payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId,
          paymentId: orderId,
          signature: "mail-signature",
          Status: "pending",
          Currency: "INR",
          Amount: total,
          PaymentMode: "payByMail",
        }),
      });

      if (!res.ok) throw new Error("Mail capture failed");

      // ✅ Save AFTER DB success
      sessionStorage.setItem("paymentId", orderId);
      sessionStorage.setItem("hemOrderId", hemOrderId);

      const emailHTML = generateEmailBody(
        "Invoice Order Book",
        hemOrderId,
        total,
        customerData,
        paymentLink,
        cartPayload,
        discount,
        actualPrice,
      );

      await sendEmail(customerData.email, "Complete Your Payment", emailHTML);

      onSuccess();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!processingPayment && (
        <div className="mt-6 bg-gray-900 text-white rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-semibold mb-4 text-center">
            Select Payment Method
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              disabled={loading}
              onClick={handleOnlinePayment}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
            >
              <CreditCard /> Online Pay
            </button>

            <button
              disabled={loading}
              onClick={() => setShowQR(true)}
              className="flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
            >
              <Banknote /> Cash Payment
            </button>

            <button
              disabled={loading}
              onClick={handlePayByMail}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
            >
              <MailIcon /> Pay by Mail
            </button>
          </div>

          {showQR && (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center">
              <div
                className="
      bg-white w-full sm:max-w-md
      h-[92vh] sm:h-auto
      rounded-t-2xl sm:rounded-2xl
      p-4 sm:p-6
      flex flex-col
      relative
      animate-slideUp
    "
              >
                {/* Close */}
                <button
                  onClick={() => setShowQR(false)}
                  className="absolute top-3 right-4 text-red-600 text-xl font-bold"
                >
                  ✕
                </button>

                {/* Title */}
                <h3 className="text-lg text-gray-600 sm:text-xl font-semibold text-center mb-2">
                  Scan to Pay
                </h3>

                <p className="text-center text-gray-600 text-sm mb-4">
                  Amount: <span className="font-bold text-black">₹{total}</span>
                </p>

                {/* QR */}
                <div className="flex justify-center my-4">
                  <QRCodeCanvas
                    value={upiURL}
                    size={window.innerWidth < 640 ? 220 : 260}
                  />
                </div>

                {/* UPI ID */}
                <p className="text-center text-xs sm:text-sm text-gray-500 break-all">
                  Banking Name: {UPI_NAME}
                </p>
                <p className="text-center text-xs sm:text-sm text-gray-500 break-all">
                  UPI ID: {UPI_ID}
                </p>

                {/* Spacer */}
                <div className="flex-grow" />

                {/* Action Button - sticky feel on mobile */}
                <button
                  onClick={handleCashPayment}
                  className="
          mt-6
          bg-green-600 hover:bg-green-700
          text-white font-semibold
          py-3 rounded-lg
          w-full
          text-base
        "
                >
                  I Have Paid
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {processingPayment && (
        <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 sm:p-8 flex flex-col items-center gap-4 text-center">
            {/* Spinner */}
            <div className="animate-spin rounded-full h-12 w-12 sm:h-14 sm:w-14 border-4 border-green-600 border-t-transparent"></div>

            {/* Main Text */}
            <p className="text-base sm:text-lg font-semibold text-gray-800">
              Generating Payment Receipt...
            </p>

            {/* Sub Text */}
            <p className="text-xs sm:text-sm text-gray-500">
              Please do not refresh or go back
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentOptions;

// import React, { useEffect, useState } from "react";
// import { CreditCard, Banknote, MailIcon } from "lucide-react";
// import generateEmailBody from "../../common/EmailBody";
// import sendEmail from "../../common/SendEmail";
// import { createRazorpayPaymentLink } from "../../common/RazorPayPartialPayment";

// const PaymentOptions = ({
//   orderId,          // Razorpay order id
//   hemOrderId,       // DB order id
//   total,
//   customerData,
//   API_BASE_URL,
//   RAZOR_PAY_API_KEY,
//   cartPayload,
//   discount,
//   actualPrice,
//   onSuccess,
// }) => {
//   const [loading, setLoading] = useState(false);

//   // ✅ Load Razorpay script once
//   useEffect(() => {
//     if (!window.Razorpay) {
//       const script = document.createElement("script");
//       script.src = "https://checkout.razorpay.com/v1/checkout.js";
//       script.async = true;
//       document.body.appendChild(script);
//     }
//   }, []);

//   // ---------------- ONLINE PAYMENT ----------------
//   const handleOnlinePayment = () => {
//     const options = {
//       key: RAZOR_PAY_API_KEY,
//       amount: total * 100,
//       currency: "INR",
//       name: "Edumitram Book Store",
//       description: "Book Order Payment",
//       order_id: orderId,

//       handler: async function (response) {
//         try {
//           // ✅ VERY IMPORTANT: Save real Razorpay payment id for slip page
//           sessionStorage.setItem("paymentId", response.razorpay_payment_id);
//           sessionStorage.setItem("orderId", hemOrderId);

//           setLoading(true);

//           await fetch(`${API_BASE_URL}/api/Order/Capture-Payment`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({
//               orderId: response.razorpay_order_id,
//               paymentId: response.razorpay_payment_id,
//               signature: response.razorpay_signature,
//               Status: "succeeded",
//               Currency: "INR",
//               Amount: total,
//               PaymentMode: "online",
//             }),
//           });

//           const emailHTML = generateEmailBody(
//             "Invoice Order Book",
//             hemOrderId,
//             total,
//             customerData,
//             null,
//             cartPayload,
//             discount,
//             actualPrice
//           );

//           await sendEmail(customerData.email, "Payment Successful", emailHTML);

//           onSuccess();
//         } catch (err) {
//           console.error(err);
//         } finally {
//           setLoading(false);
//         }
//       },

//       prefill: {
//         name: customerData.name,
//         email: customerData.email,
//         contact: customerData.mobile,
//       },
//       theme: { color: "#16a34a" },
//     };

//     const razorpay = new window.Razorpay(options);
//     razorpay.open();
//   };

//   // ---------------- CASH PAYMENT ----------------
//   const handleCashPayment = async () => {
//     try {
//       setLoading(true);

//       // ✅ Create one cash id and reuse it everywhere
//       const cashId = `cash_${Date.now()}`;

//       // ✅ Save for slip page
//       sessionStorage.setItem("paymentId", cashId);
//       sessionStorage.setItem("orderId", hemOrderId);

//       await fetch(`${API_BASE_URL}/api/Order/Capture-Payment`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           orderId,
//           paymentId: cashId, // ✅ use same id
//           signature: "cash-signature",
//           Status: "pending",
//           Currency: "INR",
//           Amount: total,
//           PaymentMode: "cashPay",
//         }),
//       });

//       const emailHTML = generateEmailBody(
//         "Cash Payment Recieved",
//         hemOrderId,
//         total,
//         customerData,
//         null,
//         cartPayload,
//         discount,
//         actualPrice
//       );

//       await sendEmail(customerData.email, "Cash Order Placed", emailHTML);

//       onSuccess();
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ---------------- PAY BY MAIL ----------------
//   const handlePayByMail = async () => {
//     try {
//       setLoading(true);

//       const paymentLink = await createRazorpayPaymentLink(
//         orderId,
//         total,
//         customerData,
//         API_BASE_URL
//       );

//       // ✅ Save reference for slip page
//       sessionStorage.setItem("paymentId", orderId);
//       sessionStorage.setItem("orderId", hemOrderId);

//       const emailHTML = generateEmailBody(
//         "Invoice Order Book",
//         hemOrderId,
//         total,
//         customerData,
//         paymentLink,
//         cartPayload,
//         discount,
//         actualPrice
//       );

//       await sendEmail(customerData.email, "Complete Your Payment", emailHTML);

//       onSuccess();
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="mt-6 bg-gray-900 text-white rounded-xl p-6 shadow-lg">
//       <h2 className="text-xl font-semibold mb-4 text-center">
//         Select Payment Method
//       </h2>

//       <div className="flex flex-col sm:flex-row gap-4 justify-center">
//         <button
//           disabled={loading}
//           onClick={handleOnlinePayment}
//           className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
//         >
//           <CreditCard /> Online Pay
//         </button>

//         <button
//           disabled={loading}
//           onClick={handleCashPayment}
//           className="flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
//         >
//           <Banknote /> Cash Payment
//         </button>

//         <button
//           disabled={loading}
//           onClick={handlePayByMail}
//           className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
//         >
//           <MailIcon /> Pay by Mail
//         </button>
//       </div>
//     </div>
//   );
// };

// export default PaymentOptions;

// import React, { useEffect, useState } from "react";
// import { CreditCard, Banknote, MailIcon } from "lucide-react";
// import generateEmailBody from "../../common/EmailBody";
// import sendEmail from "../../common/SendEmail";
// import { createRazorpayPaymentLink } from "../../common/RazorPayPartialPayment";

// const PaymentOptions = ({
//   orderId,          // Razorpay order id
//   hemOrderId,       // DB order id
//   total,
//   customerData,
//   API_BASE_URL,
//   RAZOR_PAY_API_KEY,
//   cartPayload,
//   discount,
//   actualPrice,
//   onSuccess,
// }) => {
//   const [loading, setLoading] = useState(false);

//   // Load Razorpay script once
//   useEffect(() => {
//     if (!window.Razorpay) {
//       const script = document.createElement("script");
//       script.src = "https://checkout.razorpay.com/v1/checkout.js";
//       script.async = true;
//       document.body.appendChild(script);
//     }
//   }, []);

//   // ---------------- ONLINE PAYMENT ----------------
//   const handleOnlinePayment = () => {
//     const options = {
//       key: RAZOR_PAY_API_KEY,
//       amount: total * 100,
//       currency: "INR",
//       name: "Edumitram Book Store",
//       description: "Book Order Payment",
//       order_id: orderId,

//       handler: async function (response) {
//         try {
//           setLoading(true);

//           await fetch(`${API_BASE_URL}/api/Order/Capture-Payment`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({
//               orderId: response.razorpay_order_id,
//               paymentId: response.razorpay_payment_id,
//               signature: response.razorpay_signature,
//               Status: "succeeded",
//               Currency: "INR",
//               Amount: total,
//               PaymentMode: "online",
//             }),
//           });

//           const emailHTML = generateEmailBody(
//             "Invoice Order Book",
//             hemOrderId,
//             total,
//             customerData,
//             null,
//             cartPayload,
//             discount,
//             actualPrice
//           );

//           await sendEmail(customerData.email, "Payment Successful", emailHTML);

//           onSuccess();
//         } catch (err) {
//           console.error(err);
//         } finally {
//           setLoading(false);
//         }
//       },

//       prefill: {
//         name: customerData.name,
//         email: customerData.email,
//         contact: customerData.mobile,
//       },
//       theme: { color: "#16a34a" },
//     };

//     const razorpay = new window.Razorpay(options);
//     razorpay.open();
//   };

//   // ---------------- CASH PAYMENT ----------------
//   const handleCashPayment = async () => {
//     try {
//       setLoading(true);

//       await fetch(`${API_BASE_URL}/api/Order/Capture-Payment`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           orderId,
//           paymentId: `cash_${Date.now()}`,
//           signature: "cash-signature",
//           Status: "pending",
//           Currency: "INR",
//           Amount: total,
//           PaymentMode: "cashPay",
//         }),
//       });

//       const emailHTML = generateEmailBody(
//         "Cash Payment Recieved",
//         hemOrderId,
//         total,
//         customerData,
//         null,
//         cartPayload,
//         discount,
//         actualPrice
//       );

//       await sendEmail(customerData.email, "Cash Order Placed", emailHTML);

//       onSuccess();
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ---------------- PAY BY MAIL ----------------
//   const handlePayByMail = async () => {
//     try {
//       setLoading(true);

//       const paymentLink = await createRazorpayPaymentLink(
//         orderId,
//         total,
//         customerData,
//         API_BASE_URL
//       );

//       const emailHTML = generateEmailBody(
//         "Invoice Order Book",
//         hemOrderId,
//         total,
//         customerData,
//         paymentLink,
//         cartPayload,
//         discount,
//         actualPrice
//       );

//       await sendEmail(customerData.email, "Complete Your Payment", emailHTML);

//       onSuccess();
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="mt-6 bg-gray-900 text-white rounded-xl p-6 shadow-lg">
//       <h2 className="text-xl font-semibold mb-4 text-center">
//         Select Payment Method
//       </h2>

//       <div className="flex flex-col sm:flex-row gap-4 justify-center">
//         <button
//           disabled={loading}
//           onClick={handleOnlinePayment}
//           className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
//         >
//           <CreditCard /> Online Pay
//         </button>

//         <button
//           disabled={loading}
//           onClick={handleCashPayment}
//           className="flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
//         >
//           <Banknote /> Cash Payment
//         </button>

//         <button
//           disabled={loading}
//           onClick={handlePayByMail}
//           className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg transition disabled:opacity-50"
//         >
//           <MailIcon /> Pay by Mail
//         </button>
//       </div>
//     </div>
//   );
// };

// export default PaymentOptions;
